import React from 'react'
import { ChevronRight, ChevronDown } from 'lucide-react'
import { Spinner } from '../Spinner'
import { TableCell } from './TableCell'
import { Pagination } from './Pagination'
import type { GroupedTableProps, GroupedTableColumn } from './types'

export function GroupedTable<T extends { id: string | number }, G>({
  groups,
  items,
  columns,
  getGroupKey,
  getGroupVariantCount,
  renderGroupCells,
  renderItemCells,
  expandedGroups,
  loadingGroups = new Set(),
  onGroupToggle,
  expandedItems,
  isGrouped = true,
  groupRowClassName = 'bg-gray-50 hover:bg-gray-100',
  emptyState,
  pagination,
  stickyHeader = false,
  tableLayout = 'fixed',
  className = '',
}: GroupedTableProps<T, G>) {
  // Build columns for header
  const headerColumns = isGrouped
    ? [
        { key: '__expand', title: '', width: '32px', className: '!px-1' } as GroupedTableColumn<T>,
        ...columns,
      ]
    : columns

  const hasData = isGrouped ? groups.length > 0 : (items?.length ?? 0) > 0

  return (
    <div className={`w-full ${className}`}>
      <div className="overflow-x-auto">
        <table className={`w-full ${tableLayout === 'fixed' ? 'table-fixed' : 'table-auto'}`}>
          <thead className={stickyHeader ? 'sticky top-0 z-10' : ''}>
            <tr>
              {headerColumns.map((col) => (
                <TableCell
                  key={col.key}
                  isHeader
                  align={col.align}
                  className={col.className || ''}
                  style={col.width ? { width: col.width } : undefined}
                >
                  {col.title}
                </TableCell>
              ))}
            </tr>
          </thead>
          <tbody>
            {!hasData ? null : isGrouped ? (
              // Grouped view
              groups.map((group) => {
                const groupKey = getGroupKey(group)
                const isExpanded = expandedGroups.has(groupKey)
                const isLoading = loadingGroups.has(groupKey)
                const groupItems = expandedItems?.[groupKey]
                const variantCount = getGroupVariantCount?.(group) ?? 0

                // Single-item group: render as flat row if item data is available
                if (variantCount === 1 && groupItems?.[0]) {
                  const item = groupItems[0]
                  return (
                    <tr key={groupKey} className="group bg-background hover:bg-gray-50 transition-colors duration-150">
                      <TableCell className="!px-1" style={{ width: '32px' }}>{null}</TableCell>
                      {renderItemCells(item, false)}
                    </tr>
                  )
                }

                // Multi-item group: render as collapsible
                return (
                  <React.Fragment key={groupKey}>
                    {/* Group header row */}
                    <tr
                      className={`group ${groupRowClassName} cursor-pointer transition-colors duration-150`}
                      onClick={() => onGroupToggle(groupKey)}
                    >
                      <TableCell className="!px-1" style={{ width: '32px' }}>
                        <button
                          type="button"
                          className="flex items-center justify-center p-1 rounded hover:bg-gray-200 transition-colors"
                          aria-label={isExpanded ? 'Recolher grupo' : 'Expandir grupo'}
                          aria-expanded={isExpanded}
                        >
                          {isLoading ? (
                            <Spinner size="small" color="subdued" />
                          ) : isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-gray-500" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-500" />
                          )}
                        </button>
                      </TableCell>
                      {renderGroupCells(group)}
                    </tr>

                    {/* Expanded item rows */}
                    {isExpanded &&
                      groupItems?.map((item) => (
                        <tr
                          key={item.id}
                          className="group bg-background border-l-2 border-l-violet-200 hover:bg-gray-50 transition-colors duration-150"
                        >
                          <TableCell className="!px-1" style={{ width: '32px' }}>{null}</TableCell>
                          {renderItemCells(item, true)}
                        </tr>
                      ))}
                  </React.Fragment>
                )
              })
            ) : (
              // Flat view
              items?.map((item) => (
                <tr key={item.id} className="group bg-background hover:bg-gray-50 transition-colors duration-150">
                  {renderItemCells(item, false)}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {!hasData && emptyState}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={pagination.onPageChange}
          totalItems={pagination.totalItems}
          itemsPerPage={pagination.itemsPerPage}
          hasNext={pagination.currentPage < pagination.totalPages}
          hasPrevious={pagination.currentPage > 1}
        />
      )}
    </div>
  )
}
